import React from 'react';
import { 
  FileText, 
  Kanban, 
  MessageSquare, 
  Calendar,
  TrendingUp,
  Clock,
  Target,
  Brain,
  CheckSquare,
  AlertCircle,
  Flag,
  GraduationCap,
  MapPin,
  User,
  ClipboardList
} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const { colors } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Mock data for academic tasks and subjects
  const subjects = [
    { id: '1', name: 'Matemáticas Avanzadas', color: colors.blue, professor: 'Dr. García' },
    { id: '2', name: 'Programación Web', color: colors.green, professor: 'Ing. López' },
    { id: '3', name: 'Base de Datos', color: colors.mauve, professor: 'Dra. Martínez' },
    { id: '4', name: 'Diseño UI/UX', color: colors.pink, professor: 'Prof. Rodríguez' }
  ];

  const tasks = [
    {
      id: '1',
      title: 'Proyecto Final React',
      subjectId: '2',
      dueDate: '2025-02-15',
      priority: 'high',
      status: 'in-progress',
      deliveryLocation: 'Plataforma Virtual'
    },
    {
      id: '2',
      title: 'Examen Parcial',
      subjectId: '1',
      dueDate: '2025-01-25',
      priority: 'urgent',
      status: 'pending',
      deliveryLocation: 'Aula A-101'
    },
    {
      id: '3',
      title: 'Diseño de BD',
      subjectId: '3',
      dueDate: '2025-02-01',
      priority: 'medium',
      status: 'pending',
      deliveryLocation: 'Oficina Profesora'
    },
    {
      id: '4',
      title: 'Prototipo App Móvil',
      subjectId: '4',
      dueDate: '2025-01-30',
      priority: 'high',
      status: 'completed',
      deliveryLocation: 'Presentación Online'
    },
    {
      id: '5',
      title: 'Ensayo Algoritmos',
      subjectId: '1',
      dueDate: '2025-01-28',
      priority: 'medium',
      status: 'pending',
      deliveryLocation: 'Email Profesor'
    }
  ];

  const todos = [
    { id: '1', text: 'Revisar apuntes de matemáticas', completed: false, priority: 'high' },
    { id: '2', text: 'Comprar materiales para proyecto', completed: true, priority: 'medium' },
    { id: '3', text: 'Preparar presentación final', completed: false, priority: 'high' },
    { id: '4', text: 'Hacer ejercicio', completed: false, priority: 'low' }
  ];

  const stats = [
    { label: 'Total Notes', value: '127', icon: FileText, change: '+12%', color: colors.blue },
    { label: 'Active Projects', value: '8', icon: Kanban, change: '+3', color: colors.green },
    { label: 'AI Conversations', value: '34', icon: MessageSquare, change: '+8', color: colors.mauve },
    { label: 'Hours Studied', value: '156', icon: Clock, change: '+24h', color: colors.yellow }
  ];

  const academicStats = [
    { 
      label: 'Tareas Pendientes', 
      value: tasks.filter(t => t.status !== 'completed').length.toString(), 
      icon: ClipboardList, 
      change: '+2', 
      color: colors.red 
    },
    { 
      label: 'Materias Activas', 
      value: subjects.length.toString(), 
      icon: GraduationCap, 
      change: '4', 
      color: colors.blue 
    },
    { 
      label: 'Tareas Completadas', 
      value: tasks.filter(t => t.status === 'completed').length.toString(), 
      icon: CheckSquare, 
      change: '+1', 
      color: colors.green 
    },
    { 
      label: 'Tareas Urgentes', 
      value: tasks.filter(t => t.priority === 'urgent' && t.status !== 'completed').length.toString(), 
      icon: AlertCircle, 
      change: '1', 
      color: colors.peach 
    }
  ];

  const recentNotes = [
    { id: '1', title: 'Advanced React Patterns', updatedAt: '2 hours ago', tags: ['React', 'JavaScript'] },
    { id: '2', title: 'Machine Learning Fundamentals', updatedAt: '5 hours ago', tags: ['ML', 'Python'] },
    { id: '3', title: 'Database Design Principles', updatedAt: '1 day ago', tags: ['Database', 'SQL'] },
    { id: '4', title: 'UI/UX Best Practices', updatedAt: '2 days ago', tags: ['Design', 'UX'] }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return colors.red;
      case 'high': return colors.peach;
      case 'medium': return colors.yellow;
      case 'low': return colors.green;
      default: return colors.blue;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return colors.green;
      case 'in-progress': return colors.blue;
      case 'pending': return colors.yellow;
      default: return colors.subtext0;
    }
  };

  const getSubjectById = (id: string) => subjects.find(s => s.id === id);

  const upcomingTasks = tasks
    .filter(task => task.status !== 'completed')
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
    .slice(0, 5);

  const getDaysUntilDue = (dueDate: string) => {
    const days = Math.ceil((new Date(dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const pendingTodos = todos.filter(todo => !todo.completed);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
            ¡Bienvenido de nuevo, {user?.name}!
          </h1>
          <p className="mt-1" style={{ color: colors.subtext0 }}>
            Aquí está el resumen de tus actividades académicas.
          </p>
        </div>
        
        <div 
          className="flex items-center gap-3 px-4 py-3 rounded-lg"
          style={{ backgroundColor: colors.surface0 }}
        >
          <Brain className="w-6 h-6" style={{ color: colors.blue }} />
          <div>
            <div className="text-sm font-medium" style={{ color: colors.text }}>
              Asistente IA
            </div>
            <div className="text-xs" style={{ color: colors.subtext0 }}>
              Listo para ayudar
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <button
          onClick={() => navigate('/notes')}
          className="flex items-center gap-4 p-6 rounded-xl border hover:scale-105 transition-transform"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <FileText className="w-8 h-8" style={{ color: colors.blue }} />
          <div className="text-left">
            <div className="text-lg font-semibold" style={{ color: colors.text }}>Nueva Nota</div>
            <div className="text-sm" style={{ color: colors.subtext0 }}>Captura tus ideas</div>
          </div>
        </button>

        <button
          onClick={() => navigate('/projects')}
          className="flex items-center gap-4 p-6 rounded-xl border hover:scale-105 transition-transform"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <Kanban className="w-8 h-8" style={{ color: colors.green }} />
          <div className="text-left">
            <div className="text-lg font-semibold" style={{ color: colors.text }}>Nuevo Proyecto</div>
            <div className="text-sm" style={{ color: colors.subtext0 }}>Organiza tu trabajo</div>
          </div>
        </button>

        <button
          onClick={() => navigate('/chat')}
          className="flex items-center gap-4 p-6 rounded-xl border hover:scale-105 transition-transform"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <MessageSquare className="w-8 h-8" style={{ color: colors.mauve }} />
          <div className="text-left">
            <div className="text-lg font-semibold" style={{ color: colors.text }}>Chat con IA</div>
            <div className="text-sm" style={{ color: colors.subtext0 }}>Obtén ayuda</div>
          </div>
        </button>

        <button
          onClick={() => navigate('/calendar')}
          className="flex items-center gap-4 p-6 rounded-xl border hover:scale-105 transition-transform"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <Calendar className="w-8 h-8" style={{ color: colors.yellow }} />
          <div className="text-left">
            <div className="text-lg font-semibold" style={{ color: colors.text }}>Calendario</div>
            <div className="text-sm" style={{ color: colors.subtext0 }}>Revisa tu agenda</div>
          </div>
        </button>
      </div>

      {/* Academic Stats Grid */}
      <div>
        <h2 className="text-xl font-semibold mb-4" style={{ color: colors.text }}>
          Resumen Académico
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {academicStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="p-6 rounded-xl border cursor-pointer hover:scale-105 transition-transform"
                style={{ 
                  backgroundColor: colors.mantle,
                  borderColor: colors.surface0
                }}
                onClick={() => {
                  if (stat.label.includes('Tareas')) navigate('/task-board');
                  if (stat.label.includes('Materias')) navigate('/academic-calendar');
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <Icon className="w-8 h-8" style={{ color: stat.color }} />
                  <span 
                    className="text-sm font-medium px-2 py-1 rounded"
                    style={{ 
                      backgroundColor: colors.surface0,
                      color: stat.color
                    }}
                  >
                    {stat.change}
                  </span>
                </div>
                <div className="text-2xl font-bold mb-1" style={{ color: colors.text }}>
                  {stat.value}
                </div>
                <div className="text-sm" style={{ color: colors.subtext0 }}>
                  {stat.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Upcoming Tasks */}
        <div 
          className="p-6 rounded-xl border"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold" style={{ color: colors.text }}>
              Próximas Tareas
            </h2>
            <button 
              onClick={() => navigate('/task-board')}
              className="text-sm hover:underline"
              style={{ color: colors.blue }}
            >
              Ver todas
            </button>
          </div>
          
          <div className="space-y-4">
            {upcomingTasks.map((task) => {
              const subject = getSubjectById(task.subjectId);
              const daysUntil = getDaysUntilDue(task.dueDate);
              const isOverdue = daysUntil < 0;
              const isUrgent = daysUntil <= 2 && daysUntil >= 0;
              
              return (
                <div
                  key={task.id}
                  className="p-4 rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                  style={{ 
                    backgroundColor: colors.base,
                    borderColor: colors.surface1,
                    borderLeft: `4px solid ${subject?.color || colors.blue}`
                  }}
                  onClick={() => navigate('/task-board')}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium" style={{ color: colors.text }}>
                      {task.title}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span 
                        className="text-xs px-2 py-1 rounded"
                        style={{ 
                          backgroundColor: getPriorityColor(task.priority) + '20',
                          color: getPriorityColor(task.priority)
                        }}
                      >
                        {task.priority}
                      </span>
                      <span 
                        className="text-xs px-2 py-1 rounded"
                        style={{ 
                          backgroundColor: getStatusColor(task.status) + '20',
                          color: getStatusColor(task.status)
                        }}
                      >
                        {task.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-2 text-sm" style={{ color: colors.subtext0 }}>
                    <GraduationCap className="w-3 h-3" />
                    {subject?.name}
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2" style={{ color: colors.subtext0 }}>
                      <Calendar className="w-3 h-3" />
                      <span className={isOverdue ? 'text-red-500 font-medium' : isUrgent ? 'text-orange-500 font-medium' : ''}>
                        {new Date(task.dueDate).toLocaleDateString()}
                        {isOverdue && ' (Vencida)'}
                        {isUrgent && ` (${daysUntil} días)`}
                        {daysUntil > 2 && ` (${daysUntil} días)`}
                      </span>
                    </div>
                    
                    {task.deliveryLocation && (
                      <div className="flex items-center gap-1" style={{ color: colors.subtext0 }}>
                        <MapPin className="w-3 h-3" />
                        <span className="text-xs">{task.deliveryLocation}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            
            {upcomingTasks.length === 0 && (
              <div className="text-center py-8">
                <CheckSquare className="w-12 h-12 mx-auto mb-3" style={{ color: colors.green }} />
                <p style={{ color: colors.text }}>
                  ¡Todas las tareas completadas!
                </p>
                <p className="text-sm" style={{ color: colors.subtext0 }}>
                  No hay tareas pendientes
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Notes */}
        <div 
          className="p-6 rounded-xl border"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold" style={{ color: colors.text }}>
              Recent Notes
            </h2>
            <button 
              onClick={() => navigate('/notes')}
              className="text-sm hover:underline"
              style={{ color: colors.blue }}
            >
              View all
            </button>
          </div>
          
          <div className="space-y-4">
            {recentNotes.map((note) => (
              <div
                key={note.id}
                className="flex items-center justify-between p-4 rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                style={{ 
                  backgroundColor: colors.base,
                  borderColor: colors.surface1
                }}
                onClick={() => navigate('/notes')}
              >
                <div className="flex-1">
                  <h3 className="font-medium mb-1" style={{ color: colors.text }}>
                    {note.title}
                  </h3>
                  <div className="flex items-center gap-4">
                    <span className="text-sm" style={{ color: colors.subtext0 }}>
                      {note.updatedAt}
                    </span>
                    <div className="flex gap-2">
                      {note.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs px-2 py-1 rounded"
                          style={{ 
                            backgroundColor: colors.surface0,
                            color: colors.text
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <FileText className="w-5 h-5" style={{ color: colors.subtext0 }} />
              </div>
            ))}
          </div>
        </div>

        {/* Simple Todo List */}
        <div 
          className="p-6 rounded-xl border"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold" style={{ color: colors.text }}>
              Lista Rápida
            </h2>
            <button 
              onClick={() => navigate('/simple-todo')}
              className="text-sm hover:underline"
              style={{ color: colors.blue }}
            >
              Ver todas
            </button>
          </div>
          
          <div className="space-y-3">
            {pendingTodos.slice(0, 4).map((todo) => (
              <div
                key={todo.id}
                className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                style={{ 
                  backgroundColor: colors.base,
                  borderColor: colors.surface1
                }}
                onClick={() => navigate('/simple-todo')}
              >
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: getPriorityColor(todo.priority) }}
                />
                <span className="flex-1 text-sm" style={{ color: colors.text }}>
                  {todo.text}
                </span>
                <Flag 
                  className="w-3 h-3" 
                  style={{ color: getPriorityColor(todo.priority) }} 
                />
              </div>
            ))}
            
            {pendingTodos.length === 0 && (
              <div className="text-center py-4">
                <CheckSquare className="w-8 h-8 mx-auto mb-2" style={{ color: colors.green }} />
                <p className="text-sm" style={{ color: colors.subtext0 }}>
                  No hay tareas pendientes
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subjects Overview */}
      <div 
        className="p-6 rounded-xl border"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold" style={{ color: colors.text }}>
            Materias Activas
          </h2>
          <button 
            onClick={() => navigate('/academic-calendar')}
            className="text-sm hover:underline"
            style={{ color: colors.blue }}
          >
            Ver calendario
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {subjects.map((subject) => {
            const subjectTasks = tasks.filter(task => task.subjectId === subject.id);
            const pendingTasks = subjectTasks.filter(task => task.status !== 'completed');
            const completedTasks = subjectTasks.filter(task => task.status === 'completed');
            
            return (
              <div
                key={subject.id}
                className="p-4 rounded-lg border cursor-pointer hover:scale-105 transition-transform"
                style={{ 
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  borderLeft: `4px solid ${subject.color}`
                }}
                onClick={() => navigate('/academic-calendar')}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: subject.color }}
                  />
                  <h3 className="font-medium" style={{ color: colors.text }}>
                    {subject.name}
                  </h3>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2" style={{ color: colors.subtext0 }}>
                    <User className="w-3 h-3" />
                    {subject.professor}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span style={{ color: colors.subtext0 }}>
                      {subjectTasks.length} tareas
                    </span>
                    <div className="flex items-center gap-2">
                      <span 
                        className="text-xs px-2 py-1 rounded"
                        style={{ 
                          backgroundColor: pendingTasks.length > 0 ? colors.red + '20' : colors.green + '20',
                          color: pendingTasks.length > 0 ? colors.red : colors.green
                        }}
                      >
                        {pendingTasks.length} pendientes
                      </span>
                    </div>
                  </div>
                  
                  {subjectTasks.length > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-2" style={{ backgroundColor: colors.surface0 }}>
                      <div 
                        className="h-2 rounded-full transition-all"
                        style={{ 
                          backgroundColor: subject.color,
                          width: `${(completedTasks.length / subjectTasks.length) * 100}%`
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;